/**
 * The receipt surface — what happened when the button was pressed
 * (`09-tms-write-paths.md` §5.1).
 *
 * > *"After the press — **a persistent receipt, not just a toast.** The mockups only produce
 * > toasts, which vanish in 2.8 seconds and leave no evidence."*
 *
 * So this stays on the panel until something else is pressed. It reports the outcome in the
 * receipt's **own words** — `detail` is what TMS or the gateway said, and it is rendered
 * verbatim, never paraphrased and never softened. A failed push says plainly that nothing
 * reached TMS, because §5.1's closing rule is that *"a failed push must never leave the app
 * claiming TMS holds what is on screen"*.
 *
 * The at-a-glance chips (`✓ Marks Pushed {HH:MM}`, `✓ Last Pushed {HH:MM}`) remain where they
 * are, beside the panel title; this is the account beneath them.
 */

import type { PushReceipt } from "../../api/types.js";
import { tournamentTimeOfDay } from "../../app/tournamentTime.js";

/**
 * `09:12` — the same 24-hour rendering the chips use, on the tournament's clock.
 *
 * **RUN 83.** `toLocaleTimeString` with no `timeZone` renders in the DEVICE's, so the receipt
 * beside the button and the receipt list on the profile — which both come through here — told
 * the TD his push landed at a time nobody at the venue could have pressed it. A receipt is
 * evidence of when something was sent; the one thing it must not be is nine hours out.
 */
export function pushTime(at: string): string {
  const pressed = new Date(at);
  // RUN 83 QA — an unreadable instant is PRINTED, never thrown on. `toLocaleTimeString` used to
  // answer "Invalid Date" and the chip rendered; `formatToParts` raises `RangeError` instead, and
  // there is no error boundary in this app, so a throw here unmounts the root and the TD loses the
  // whole Control Centre over one bad stamp. `receiptStamp` already guarded for this reason; the
  // guard belongs in here, where all three callers inherit it.
  if (Number.isNaN(pressed.getTime())) return at;
  return tournamentTimeOfDay(pressed);
}

/**
 * What the TD should do next, by failure reason (§5.1's failure table). Each one names the
 * remedy rather than restating the problem — the detail above it has already done that.
 */
const NEXT_STEP: Readonly<Record<string, string>> = {
  transport_unavailable:
    "Use Copy For TMS or Download Word below and paste it into TMS yourself. Nothing is lost.",
  session_expired: "Sign in to TMS in your browser, then press again — the payload is preserved.",
  unreachable: "Try again when the connection is back, or use the fallbacks below.",
  form_changed:
    "Nothing was written: the TMS form did not look as expected, and a push never proceeds " +
    "from a form we could not read in full. Use the fallbacks below.",
  rejected_by_tms: "TMS refused it. Use the fallbacks below, or correct the record and press again.",
};

export function PushReceiptNote({ receipt }: { receipt: PushReceipt }): React.JSX.Element {
  const accepted = receipt.outcome === "accepted_draft";

  return (
    <div
      className={`pushreceipt ${accepted ? "pushreceipt--good" : "pushreceipt--fail"}`}
      role={accepted ? "status" : "alert"}
      data-testid="push-receipt"
    >
      <b>
        {accepted
          ? `Pushed As Draft · ${pushTime(receipt.pressedAt)}`
          : `Not Pushed · ${pushTime(receipt.pressedAt)}`}
      </b>
      <p data-testid="push-receipt-detail">{receipt.detail}</p>
      <p className="pushreceipt__summary">{receipt.payloadSummary}</p>
      {accepted ? null : (
        <p className="pushreceipt__next" data-testid="push-receipt-next">
          {NEXT_STEP[receipt.failureReason ?? ""] ??
            "Nothing reached TMS. Use the fallbacks below."}
        </p>
      )}
      {accepted && receipt.tmsReference !== null ? (
        <p className="pushreceipt__ref">TMS reference {receipt.tmsReference}</p>
      ) : null}
    </div>
  );
}
